import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicIcon from '@mui/icons-material/Mic';
import { Button, ButtonGroup, CircularProgress, Stack, Menu, MenuItem } from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid } from '@mui/x-data-grid';
import Box from "@mui/material/Box";
import moment from "moment";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import useApiHook from "../hooks/useApiHook";
import FileUploadDialog from "./FileUploadDialog";
import AudioRecorderDialog from "./AudioRecorderDialog";
import { NavLink, useNavigate } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import IconButton from "@mui/material/IconButton";
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDeleteFolderModal, useMoveFileToFolderModal, useRenameFolderModal } from "../store/modal-context";
import { useNotification } from "../store/notification-context";
import useAppView from "./platforms/useAppView";
import useDocumentVisibility from "../hooks/useDocumentVisibility";

moment.locale('it');

const FileList = ({ folderId = null, scope = null, title = "Tutti i file", currentFolderName = null }) => {
    const { response, makeRequest } = useApiHook();
    const { makeRequest: makeDeleteRequest } = useApiHook();
    const moveFileToFolderModal = useMoveFileToFolderModal();
    const renameFolderModal = useRenameFolderModal();
    const deleteFolderModal = useDeleteFolderModal();
    const { notifyTranscriptionCompleted } = useNotification();
    const { isAppView } = useAppView();
    const isDocumentVisible = useDocumentVisibility();
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [recorderDialogOpen, setRecorderDialogOpen] = useState(false);
    const [folderOptionsAnchorEl, setFolderOptionsAnchorEl] = useState(null);
    const navigate = useNavigate();
    const pollingIntervalRef = useRef(null);
    const previousHasWaitingRef = useRef(false);
    const previousFilesRef = useRef([]);
    const backgroundedDuringPollingRef = useRef(false);
    const targetFolderId = folderId && scope !== 'root' ? folderId : null;

    const getAllFiles = useCallback(() => {
        const requestData = {};

        if (folderId && scope !== 'root') {
            requestData.folderId = folderId;
        }

        if (scope) {
            requestData.scope = scope;
        }

        return makeRequest('/file/getAll', 'get', requestData);
    }, [folderId, makeRequest, scope]);

    const files = useMemo(() => {
        if (!response || !Array.isArray(response)) return [];
        return response.map(({ id, name, folderId, folderName, createdAt, status, duration }) => ({
            id,
            info: {
                name,
                folderId,
                folderName
            },
            uploadDate: new Date(createdAt),
            state: status,
            duration: duration || "0:0"
        }));
    }, [response]);

    const deleteBtnClickHandler = useCallback(async (fileId) => {
        await makeDeleteRequest('/file/delete', 'post', { fileId });
        await getAllFiles();
    }, [getAllFiles, makeDeleteRequest]);

    const handleDelete = useCallback(async (ev, fileId) => {
        ev.stopPropagation();
        await deleteBtnClickHandler(fileId);
    }, [deleteBtnClickHandler]);

    const handleChangeFolder = useCallback((ev, fileId, fileName, currentFolderId = null) => {
        ev.stopPropagation();
        moveFileToFolderModal.openModal({
            fileId,
            folderId: currentFolderId,
            fileName,
            onSuccess: getAllFiles
        });
    }, [getAllFiles, moveFileToFolderModal]);

    const webColumns = useMemo(() => [
        {
            field: 'info',
            headerName: 'Nome file',
            flex: 1,
            renderCell: (({ value }) => {
                return <Stack height={'100%'} spacing={0} direction={'column'} justifyContent={'center'} alignItems={'start'}>
                    {value.folderId && <NavLink
                        to={`/folder/${value.folderId}`}
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <Stack direction={'row'} alignItems={'center'} spacing={1} color={'black'} sx={{
                            opacity: .7,
                            transition: 'all ease-out .3s',
                            '&:hover': { opacity: 1 }
                        }}>
                            <FolderOutlinedIcon size={'small'} />
                            <Typography variant='body2' fontSize={".8rem"}>{value.folderName}</Typography>
                        </Stack>
                    </NavLink>}
                    <Typography variant='body1'>{value.name}</Typography>
                </Stack>
            })
        },
        {
            field: 'uploadDate',
            headerName: 'Data',
            renderCell: ({ value }) => {
                return <Stack spacing={0} alignItems={'center'} justifyContent={'center'}>
                    <Typography variant='body2'>{moment(value).format('D MMMM YYYY')}</Typography>
                    <Typography variant='body2'>{moment(value).format('HH:mm:ss')}</Typography>
                </Stack>
            }
        },
        {
            field: 'duration',
            headerName: 'Durata',
            width: 80,
            valueFormatter: (duration) => {
                if (!duration) return "-";
                const totalSeconds = parseInt(duration);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return `${minutes !== 0 ? minutes + "m " : ""}${seconds}s`;
            }
        },
        {
            field: 'state',
            headerName: 'Stato',
            width: 60,
            renderCell: ({ value }) => {
                return (value === 'waiting') ? <CircularProgress sx={{ m: "1px" }} size={20} /> : <CheckCircleIcon color="success" />;
            }
        },
        {
            field: 'actions',
            headerName: 'Azioni',
            width: 100,
            renderCell: ({ row }) => {
                return <ButtonGroup variant="text">
                    <IconButton color="error" aria-label="delete" onClick={(ev) => handleDelete(ev, row.id)}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton color="info" aria-label="upload" onClick={(ev) => handleChangeFolder(ev, row.id, row.info.name, row.info.folderId)}>
                        <DriveFolderUploadOutlinedIcon />
                    </IconButton>
                </ButtonGroup>
            }
        }
    ], [handleChangeFolder, handleDelete]);

    const mobileColumns = useMemo(() => [
        {
            field: 'info',
            headerName: 'Nome file',
            width: 150,
            renderCell: ({ value }) => {
                return <Stack height={'100%'} spacing={0} direction={'column'} justifyContent={'center'} alignItems={'start'}>
                    {value.folderId && <NavLink
                        to={`/folder/${value.folderId}`}
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <Stack direction={'row'} alignItems={'center'} spacing={1} color={'black'} sx={{
                            opacity: .7,
                            transition: 'all ease-out .3s',
                            '&:hover': { opacity: 1 }
                        }}>
                            <FolderOutlinedIcon size={'small'} />
                            <Typography variant='body2' fontSize={".8rem"}>{value.folderName}</Typography>
                        </Stack>
                    </NavLink>}
                    <Typography variant='body1'>{value.name}</Typography>
                </Stack>
            }
        },
        {
            field: 'duration',
            headerName: 'Durata',
            width: 100,
            valueFormatter: (duration) => {
                if (!duration) return "-";
                const totalSeconds = parseInt(duration);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return `${minutes !== 0 ? minutes + "m " : ""}${seconds}s`;
            }
        },
        {
            field: 'uploadDate',
            headerName: 'Data',
            valueFormatter: (date) => {
                return moment(date).format('D MMMM YYYY, HH:mm:ss');
            }
        },
        {
            field: 'actions',
            headerName: 'Azioni',
            renderCell: ({ row }) => {
                return <ButtonGroup variant="text">
                    <IconButton color="error" aria-label="delete" onClick={(ev) => handleDelete(ev, row.id)}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton color="info" aria-label="move-to-folder" onClick={(ev) => handleChangeFolder(ev, row.id, row.info.name, row.info.folderId)}>
                        <DriveFolderUploadOutlinedIcon />
                    </IconButton>
                </ButtonGroup>
            }
        }
    ], [handleChangeFolder, handleDelete]);

    useEffect(() => {
        getAllFiles();
    }, [getAllFiles]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        getAllFiles();
        pollingIntervalRef.current = setInterval(() => {
            getAllFiles();
        }, 3000);
    }, [getAllFiles, stopPolling]);

    useEffect(() => {
        const hasWaitingFiles = files.some(file => file.state === 'waiting');

        if (hasWaitingFiles && !previousHasWaitingRef.current) {
            startPolling();
        } else if (!hasWaitingFiles && previousHasWaitingRef.current) {
            stopPolling();
        }

        previousHasWaitingRef.current = hasWaitingFiles;
    }, [files, startPolling, stopPolling]);

    useEffect(() => {
        if (!isDocumentVisible && previousHasWaitingRef.current) {
            backgroundedDuringPollingRef.current = true;
        }
    }, [isDocumentVisible]);

    useEffect(() => {
        files.forEach((currentFile) => {
            const previousFile = previousFilesRef.current.find((file) => file.id === currentFile.id);

            if (previousFile?.state === 'waiting' && currentFile.state === 'done') {
                if (!isDocumentVisible || !backgroundedDuringPollingRef.current) {
                    notifyTranscriptionCompleted({
                        fileId: currentFile.id,
                        fileName: currentFile.info?.name
                    });
                }
            }
        });

        previousFilesRef.current = files;
        if (files.every((file) => file.state !== 'waiting')) {
            backgroundedDuringPollingRef.current = false;
        }
    }, [files, isDocumentVisible, notifyTranscriptionCompleted]);

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, []);

    const onFileUpload = useCallback(() => {
        getAllFiles();
    }, [getAllFiles]);

    const uploadFileBtnClickHandler = () => {
        setUploadDialogOpen(true);
    }

    const recordBtnClickHandler = () => {
        setRecorderDialogOpen(true);
    }

    const folderOptionsOpen = Boolean(folderOptionsAnchorEl);

    const openFolderOptionsHandler = (event) => {
        setFolderOptionsAnchorEl(event.currentTarget);
    };

    const closeFolderOptionsHandler = () => {
        setFolderOptionsAnchorEl(null);
    };

    const renameFolderClickHandler = () => {
        closeFolderOptionsHandler();
        renameFolderModal.openModal({
            folderId,
            folderName: currentFolderName,
        });
    };

    const deleteFolderClickHandler = () => {
        closeFolderOptionsHandler();
        deleteFolderModal.openModal({
            folderId,
            folderName: currentFolderName,
            onSuccess: () => navigate('/dashboard')
        });
    };

    const rowClickHandler = (params) => {
        navigate(`/file/${params.id}`);
    };

    const isFolderView = Boolean(folderId) && scope !== 'root';
    const noRowsOverlay = () => (
        <Stack height="100%" alignItems="center" justifyContent="center">
            <Typography variant="body1" sx={{ opacity: 0.65 }}>
                {isFolderView ? 'Ancora nessun file in questa cartella' : 'Nessun file disponibile'}
            </Typography>
        </Stack>
    );

    return <>
        <FileUploadDialog currentFolderId={targetFolderId} currentFolderName={currentFolderName} open={uploadDialogOpen} onFileUpload={onFileUpload} onClose={() => setUploadDialogOpen(false)}></FileUploadDialog>
        <AudioRecorderDialog currentFolderId={targetFolderId} currentFolderName={currentFolderName} open={recorderDialogOpen} onFileUpload={onFileUpload} onClose={() => setRecorderDialogOpen(false)}></AudioRecorderDialog>

        <Stack spacing={2} direction={{ sm: 'row' }} flexWrap={'wrap'} alignItems="center" justifyContent={'end'}>
            <Typography flexGrow={1} variant='h4' fontWeight='bold' sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>{title}</Typography>
            <Button
                variant="outlined"
                onClick={recordBtnClickHandler}
                startIcon={<MicIcon />}>
                Registra
            </Button>
            <Button
                variant="outlined"
                onClick={uploadFileBtnClickHandler}
                startIcon={<UploadFileIcon />}>
                Carica file
            </Button>
            {(folderId && scope !== 'root') && <>
                <Button
                    variant="outlined"
                    onClick={openFolderOptionsHandler}
                    startIcon={<MoreVertIcon />}>
                    Opzioni
                </Button>
                <Menu
                    anchorEl={folderOptionsAnchorEl}
                    open={folderOptionsOpen}
                    onClose={closeFolderOptionsHandler}
                >
                    <MenuItem onClick={renameFolderClickHandler}>Rinomina cartella</MenuItem>
                    <MenuItem onClick={deleteFolderClickHandler}>Elimina cartella</MenuItem>
                </Menu>
            </>}
        </Stack>
        <Typography variant='h6' fontWeight='bold' mb={2}>File</Typography>
        <Box mb={3}>
            <DataGrid
                onRowClick={rowClickHandler}
                rows={files}
                getRowHeight={() => 72}
                columns={isAppView ? mobileColumns : webColumns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 10
                        }
                    }
                }}
                pageSizeOptions={[5, 10, 15]}
                disableRowSelectionOnClick
                slots={{
                    noRowsOverlay
                }}
            />
        </Box>
    </>
}

export default FileList;
